import { Button, Icon, RequestStatusIcon } from "@components/library";
import { COLORS, FEATURE_FLAGS, FONTS, WINDOW_DIMENSIONS } from "@constants";
import { RootState } from "@redux/store";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { RequestForCard } from "@tsTypes/index";
import { LaunchableRequestStatus } from "@tsTypes/requests";
import { MarketplaceRequest } from "@tsTypes/requests/marketplace";
import { UserRole } from "@tsTypes/users";
import { track } from "@utils/appUtils";
import { getLaunchableRequestStatus } from "@utils/requestUtils";
import { assertAbsurd } from "@utils/tsUtils";
import dayjs from "dayjs";
import { Dispatch, ReactNode, SetStateAction } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import RefineRecommendations from "./RefineRecommendations";

interface Props {
  request: RequestForCard | MarketplaceRequest;
  isUserFollowing: boolean;
  isCardHovered: boolean;
  setShouldFade: Dispatch<SetStateAction<boolean>>;
  shouldShowUnsubscribeButton?: boolean;
}

const RequestCardFooter = ({
  request,
  isUserFollowing,
  isCardHovered,
  setShouldFade,
  shouldShowUnsubscribeButton = false,
}: Props) => {
  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);

  const requestStatus = getLaunchableRequestStatus(request);

  const isTrialRfp = "is_trial" in request.requestable && request.requestable.is_trial;

  const waitListStatusEl = (
    <Status color={COLORS.GREEN_800}>
      <RequestStatusIcon status="trial" />
      Accepting first 5 proposals
    </Status>
  );

  const closedStatusEl = <Status color={COLORS.NEUTRAL_600}>Complete</Status>;

  let statusEl: ReactNode;
  switch (requestStatus) {
    case LaunchableRequestStatus.COMING_SOON: {
      statusEl = <Status color={COLORS.ORANGE}>Coming soon!</Status>;
      break;
    }
    case LaunchableRequestStatus.ACTIVE: {
      statusEl = isTrialRfp ? (
        waitListStatusEl
      ) : (
        <Status color={COLORS.GREEN_800}>
          Apply by {dayjs(request.requestable.deadline ?? 0).format("MMM D")}
        </Status>
      );
      break;
    }
    case LaunchableRequestStatus.IN_EXTENSION: {
      statusEl = isTrialRfp ? waitListStatusEl : closedStatusEl;
      break;
    }
    case LaunchableRequestStatus.CLOSED: {
      statusEl = closedStatusEl;
      break;
    }
    default: {
      assertAbsurd(requestStatus);
    }
  }

  const shouldShowNotifyButton =
    requestStatus === LaunchableRequestStatus.COMING_SOON && currentUser.role !== UserRole.SPONSOR;

  return (
    <Container data-testid="footer">
      {FEATURE_FLAGS.RELEVANT_NOT_RELEVANT_BUTTONS ? (
        <RefineRecommendations request={request} setShouldFade={setShouldFade} />
      ) : (
        <div />
      )}
      {(() => {
        if (shouldShowUnsubscribeButton) {
          return (
            <Button
              variant="destructive-ghost"
              size="sm"
              onClick={() => {
                track(SegmentEventName.CLICK, {
                  react_component: "RfpCardFooter",
                  ui_component: "Decline Updates Button",
                  request_slug: request.slug,
                });
              }}
            >
              Stop notifications
            </Button>
          );
        }
        if (shouldShowNotifyButton) {
          return isUserFollowing ? (
            <OptedIn>
              <Icon name="Success" color={COLORS.GREEN} size="sm" />
              You&apos;ll be notified
            </OptedIn>
          ) : (
            <NotifyMe>
              <Icon name="Bell" color={COLORS.HALO_BLUE} />
              <NotifyMeText isCardHovered={isCardHovered}>Notify me</NotifyMeText>
            </NotifyMe>
          );
        }
        return statusEl;
      })()}
    </Container>
  );
};

export default RequestCardFooter;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid ${COLORS.NEUTRAL_200};
  ${FONTS.SEMIBOLD_3};
`;
const Status = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ color }) => color};
`;
const OptedIn = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${COLORS.GREEN};
`;
const NotifyMe = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  color: ${COLORS.HALO_BLUE};
  pointer-events: fill;
  -webkit-transition: all 0.2s ease-in-out;
  -moz-transition: all 0.2s ease-in-out;
  -o-transition: all 0.2s ease-in-out;
  transition: all 0.2s ease-in-out;
`;
const NotifyMeText = styled.span`
  display: inline-block;
  max-width: 0;
  white-space: nowrap;
  overflow: hidden;
  -webkit-transition: 0.2s ease-in-out;
  transition: 0.2s ease-in-out;
  ${({ isCardHovered }) =>
    isCardHovered &&
    `
      max-width: 67px;
      margin-left: 4px;
  `}
  ${WINDOW_DIMENSIONS.MOBILE_MEDIA_QUERY} {
    max-width: 67px;
    margin-left: 4px;
  }
`;
